import PropTypes from 'prop-types'
import { useState, useEffect, useCallback, useRef } from 'react'
import Badge from 'react-bootstrap/Badge'
import { useParams } from 'react-router-dom'

import { ID_PERFIL_PARTICULAR } from '../../constants/perfiles'
import {
  ESTADO_CREADA,
  ESTADO_ELIMINADA,
  ESTADO_PAUSADA,
  ESTADO_PUBLICADA,
  ESTADO_VENCIDA
} from '../../constants/publicaciones'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import Imagen from '../imagen/Imagen'
import Precios from '../reutilizables/precios/Precios'
import * as favoritosService from '../servicios/favoritosService'
import styles from './Publicaciones.module.css'

const Publicacion = ({
  publicacion,
  onToggleClick,
  onDelete: onDeleteProp,
  compFavorito,
  vistaCuadricula,
  vieneDeAdministrar,
  adjunto,
  loadingImagenes
}) => {
  const { idPublicacion } = useParams()
  const { loginDataContext: { idUsuario } = {}, esPerfilAdmin } = useAuth()
  const favIcon = useRef()
  const hasProcessedURL = useRef(false)

  const [state, setState] = useState({
    show: true,
    badgeEstadoColor: '',
    imagenUrl: ''
  })

  const badgeEstado = useCallback(() => {
    let badgeEstadoColor = ''
    switch (publicacion.idPublicacionEstado) {
      case ESTADO_CREADA:
        badgeEstadoColor = 'danger'
        break
      case ESTADO_PUBLICADA:
        badgeEstadoColor = 'nuevo'
        break
      case ESTADO_PAUSADA:
        badgeEstadoColor = 'warning'
        break
      case ESTADO_ELIMINADA:
        badgeEstadoColor = 'danger'
        break
      case ESTADO_VENCIDA:
        badgeEstadoColor = 'light'
        break
      default:
        badgeEstadoColor = ''
    }
    setState((prevState) => ({
      ...prevState,
      badgeEstadoColor: badgeEstadoColor
    }))
  }, [publicacion.idPublicacionEstado])

  // Handle URL parameter toggle - only once per URL change
  useEffect(() => {
    if (
      idPublicacion &&
      publicacion.idPublicacion === parseInt(idPublicacion) &&
      !hasProcessedURL.current
    ) {
      hasProcessedURL.current = true
      onToggleClick(null, publicacion, publicacion.idPublicacion)
    }

    // Reset flag when URL parameter changes
    if (!idPublicacion || publicacion.idPublicacion !== parseInt(idPublicacion)) {
      hasProcessedURL.current = false
    }
  }, [idPublicacion, publicacion.idPublicacion, onToggleClick])

  useEffect(() => {
    badgeEstado()
  }, [badgeEstado])

  const saveFavorito = useCallback(
    async (idPub) => {
      try {
        const favorito = {
          idUsuario,
          idPublicacion: parseInt(idPub)
        }

        const { data: favoritoId } = await favoritosService.insertar(favorito)
        if (!compFavorito && favIcon.current) {
          favIcon.current.classList.remove('fa-heart', 'fa-heart-o')

          if (favoritoId === 0) {
            favIcon.current.classList.add('fa-heart-o')
          } else {
            favIcon.current.classList.add('fa-heart')
          }
        }
      } catch (error) {
        const mensaje = `Ocurrió un error al guardar favorito.`
        toastErrorLog(error, mensaje, 'favorito')
      }
    },
    [compFavorito, idUsuario]
  )

  if (!state.show) {
    return null
  }

  const favoritoClass =
    publicacion.esFavorito || compFavorito ? 'fa fa-heart favorito' : 'fa fa-heart-o favorito'

  const estadoPublicacion = compFavorito
    ? publicacion.idPublicacionEstado === ESTADO_PAUSADA
      ? 'PAUSADA'
      : publicacion.conPresupuesto || publicacion.precio <= 0
        ? 'PRESUPUESTO'
        : publicacion.cantDisponible <= 0 && !publicacion.aPedido
          ? 'SIN_STOCK'
          : 'PRECIO'
    : publicacion.precio <= 0
      ? 'PRESUPUESTO'
      : 'PRECIO'

  const esNoDisponibleEnFavoritos =
    estadoPublicacion === 'PAUSADA' || estadoPublicacion === 'SIN_STOCK'

  return (
    <div
      className={`
        ${styles.publicacionData}
        ${publicacion?.clase || ''}
        ${vistaCuadricula ? styles.columna : styles.fila}
        ${esNoDisponibleEnFavoritos ? styles.publicacionNoDisponible : ''}
      `}
      onClick={(e) => onToggleClick(e, publicacion, publicacion.idPublicacion)}
    >
      <div className={`${styles.data} ${vistaCuadricula ? styles.columna : styles.fila}`}>
        <div className={styles.contenedorImg}>
          <Imagen
            className={`${styles.img} ${!vistaCuadricula ? styles.imgSmall : ''}`}
            adjunto={adjunto}
            loadingImagenes={loadingImagenes}
          />
        </div>
        {vistaCuadricula && <p className={styles.titulo}>{publicacion.titulo}</p>}
        <div>
          {!compFavorito && vieneDeAdministrar && (
            <div className={styles.id}>#{publicacion.idPublicacion}</div>
          )}
          {!vistaCuadricula && <p className={styles.titulo}>{publicacion.titulo}</p>}

          {estadoPublicacion === 'PAUSADA' ? (
            <div className={styles.titulo_favorito_pausada}>PUBLICACIÓN PAUSADA</div>
          ) : estadoPublicacion === 'PRESUPUESTO' ? (
            <div className={styles.titulo_presupuesto}> PRESUPUESTO </div>
          ) : estadoPublicacion === 'SIN_STOCK' ? (
            <div className={styles.titulo_favorito_pausada}>SIN STOCK</div>
          ) : (
            <>
              {(() => {
                const porcDctoMostrar = publicacion.porcDctoMostrar ?? publicacion.porcDcto
                return (
                  <Precios
                    precio={publicacion.precio}
                    monedaSimbolo={publicacion.monSimb}
                    alicuota={publicacion.alicuota || 0}
                    className={styles.precio}
                    porcDcto={Number(porcDctoMostrar) || 0}
                    showSinIVA={vieneDeAdministrar && Number(publicacion.alicuota) > 0}
                    classNameSinIva={styles.precioSinIva}
                  />
                )
              })()}
            </>
          )}
          <div className={styles.badges}>
            {esPerfilAdmin &&
              vieneDeAdministrar &&
              publicacion.idVendedor !== idUsuario &&
              Boolean(publicacion.vendedorNombre) && (
                <span className={`badge badge-light ${styles.vendedorBadge}`}>
                  <i className="fa fa-user" aria-hidden="true" /> {publicacion.vendedorNombre}
                </span>
              )}
            {publicacion.esTiendaOficial && (
              <span className="badge badge-nuevo">
                <i className="fa fa-certificate" /> Tienda Oficial
              </span>
            )}
            <span className="badge badge-usado">{publicacion.nuevo ? 'Nuevo' : 'Usado'}</span>
            {publicacion.scrap && (
              <span className="badge badge-nuevo">
                <i className="fa fa-recycle" /> Scrap
              </span>
            )}
            <span className="badge badge-danger">
              {publicacion.idPerfil === ID_PERFIL_PARTICULAR ? 'Particular' : 'Empresa'}
            </span>

            {vieneDeAdministrar && (
              <>
                <span className="badge badge-dark">
                  Exposición {publicacion.publicacionExposicion}
                </span>
                {publicacion.idListaVenta && (
                  <span className="badge badge-secondary">
                    <i className="fa fa-th-list" /> {publicacion.listaVenta}
                  </span>
                )}
                <Badge variant={state.badgeEstadoColor}>{publicacion.publicacionEstado}</Badge>
                {publicacion.porcDcto > 0 && (
                  <span className="badge badge-info badgePublicaciones">
                    {publicacion.porcDctoLista > 0
                      ? 'Descuento de lista'
                      : 'Descuento de publicación'}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div>
        {!vieneDeAdministrar && (
          <i
            onClick={(e) => {
              e.stopPropagation()
              saveFavorito(publicacion.idPublicacion)
              if (compFavorito) {
                onDeleteProp(publicacion.idPublicacion)
              }
            }}
            className={`${styles.accion} ${favoritoClass}`}
            ref={favIcon}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  )
}

Publicacion.propTypes = {
  publicacion: PropTypes.shape({
    idPublicacion: PropTypes.number.isRequired,
    idVendedor: PropTypes.number.isRequired,
    idPublicacionEstado: PropTypes.number,
    clase: PropTypes.string,
    titulo: PropTypes.string,
    monSimb: PropTypes.string,
    precio: PropTypes.number,
    alicuota: PropTypes.number,
    esTiendaOficial: PropTypes.bool,
    nuevo: PropTypes.bool,
    scrap: PropTypes.bool,
    idPerfil: PropTypes.number,
    publicacionExposicion: PropTypes.number,
    idListaVenta: PropTypes.number,
    listaVenta: PropTypes.string,
    publicacionEstado: PropTypes.string,
    esFavorito: PropTypes.bool,
    porcDcto: PropTypes.number,
    porcDctoMostrar: PropTypes.number,
    porcDctoLista: PropTypes.number,
    cantDisponible: PropTypes.number,
    conPresupuesto: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    vendedorNombre: PropTypes.string,
    aPedido: PropTypes.bool
  }).isRequired,
  onToggleClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  compFavorito: PropTypes.bool,
  vistaCuadricula: PropTypes.bool,
  vieneDeAdministrar: PropTypes.bool,
  adjunto: PropTypes.shape({
    url: PropTypes.string.isRequired,
    nombre: PropTypes.string
  }),
  loadingImagenes: PropTypes.bool
}

export default Publicacion
